import { Avatar, AvatarFallback } from '@mdvip/shared/components/ui/avatar';
import { initials } from '@mdvip/shared/lib/initials';
import { useTranslation } from 'react-i18next';
import { BlankValue } from './BlankValue';

export interface MemberIdentityCellProps {
  firstName: string | null;
  lastName: string | null;
  mdvipId: string | null;
}

/**
 * The Patient column: an initials avatar, the name as "Last, First", and the MDVIP ID beneath it.
 * Initials only — directory rows carry no usable photo. The name is plain text until the provider
 * app has a patient profile to link to.
 */
export function MemberIdentityCell({ firstName, lastName, mdvipId }: MemberIdentityCellProps) {
  const { t } = useTranslation('providers');
  const name = [lastName, firstName].filter(Boolean).join(', ');

  return (
    <div className="flex min-w-0 items-center gap-2">
      <Avatar className="size-12 shrink-0" aria-hidden="true">
        {/* Ramp steps, not the avatar tokens: a patient's avatar is always the tangerine member ramp,
            which the providers theme re-points to green. Dark initials for the same reason. */}
        <AvatarFallback className="bg-gradient-to-br from-[var(--tangerine-400)] to-[var(--tangerine-600)] text-sm font-semibold text-[var(--neutral-900)]">
          {initials({ firstName, lastName })}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0">
        <span className="block whitespace-nowrap text-sm font-semibold text-foreground">{name || <BlankValue />}</span>
        {mdvipId && (
          <span className="block text-xs text-muted-foreground">
            <span className="font-medium text-foreground">{t('memberViews.columns.mdvipIdPrefix')}</span> {mdvipId}
          </span>
        )}
      </div>
    </div>
  );
}
